import React, { useMemo } from 'react'
import { ComboBox, createUseStyles, Typography, useColorMode } from '@airbus/components-react/components'
import FormField, { FormFieldProps } from './FormField'
import { ComboBoxDefaultOption, ComboBoxProps } from '@airbus/components-react/components/ComboBox'
import FormControl from './FormControl'
import { FormControlProps } from '@airbus/components-react/components/Form'
import clsx from 'clsx'

type CustomSelectProps = ComboBoxProps<ComboBoxDefaultOption> & FormControlProps

const useStyles = createUseStyles(
  () => ({
    capitalize: {
      '& input': {
        textTransform: 'uppercase',
      },
    },
  }),
  'combobox-container',
)

export const CustomSelect = ({
  value,
  multiple,
  label,
  error,
  readOnly = false,
  labelAdornment,
  onChange,
  required = false,
  style,
  getOptionLabel,
  getChipLabel,
  capitalize = false,
  onInputValueChange,
  compact = false,
  disableOnInputChange = false,
  ...rest
}: CustomSelectProps) => {
  // This is to avoid providing undefined value to Component as it makes it uncontrolled
  const fieldValue = value || (multiple ? [] : '')
  const classes = useStyles()
  const theme = useColorMode()

  let readOnlyText = ''
  if (readOnly) {
    if (multiple) {
      if (getChipLabel)
        readOnlyText = rest.options
          ?.filter((o) => fieldValue.includes(o.value))
          .map((o) => getChipLabel(o))
          .join(', ')
      else readOnlyText = fieldValue.join(', ')
    } else {
      if (getOptionLabel) readOnlyText = getOptionLabel(fieldValue && rest.options?.find((o) => o.value === fieldValue))
      else readOnlyText = fieldValue
    }
  }

  const resolvedValue = useMemo(() => {
    if (multiple) {
      return fieldValue?.map((v) => rest.options?.find((o) => o.value === v))
    } else {
      return fieldValue
    }
  }, [fieldValue, rest.options, multiple])

  return (
    <FormControl
      label={label}
      error={error}
      labelAdornment={labelAdornment}
      readOnly={readOnly}
      required={required}
      style={style}
      className={clsx(capitalize && classes.capitalize)}
    >
      {readOnly ? (
        <Typography
          style={{
            fontWeight: 'bold',
            lineHeight: theme.size.spacing[40],
            overflowX: 'auto',
          }}
        >
          {readOnlyText || '-'}
        </Typography>
      ) : (
        <ComboBox
          value={resolvedValue}
          onChange={(v) => {
            if (onChange && v) onChange(multiple ? v.map((x) => x.value) : v.value)
          }} // Fix Airbus ComboBox is passing the whole selected option
          multiple={multiple as boolean}
          hideClear
          style={{ paddingRight: compact && '22px' }}
          getOptionLabel={getOptionLabel}
          getChipLabel={getChipLabel}
          tabIndex={0}
          onInputValueChange={({ inputValue }: { inputValue: string }) => {
            if (onInputValueChange)
              onInputValueChange({ inputValue: capitalize ? inputValue.toUpperCase() : inputValue })
            if (!disableOnInputChange && !multiple) {
              if (onChange) onChange(inputValue)
            }
          }}
          {...rest}
        />
      )}
    </FormControl>
  )
}

export const FormSelect = (props: FormFieldProps<CustomSelectProps>) => (
  <FormField {...props} Component={CustomSelect} />
)

export default FormSelect
